import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import {
  ArrowLeft, Plus, Pencil, Trash2, X, Briefcase, Tag,
  DollarSign, Info,
} from "lucide-react";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { catalogService } from "../../../services/catalogService";
import { toast } from "sonner";
import type { Service } from "../../../types";

interface ServiceModalProps {
  service?: Service;
  onClose: () => void;
  onSave: (data: {
    name: string;
    category: string;
    description: string;
    price: string;
    tags: string[];
  }) => Promise<void>;
}

function ServiceModal({ service, onClose, onSave }: ServiceModalProps) {
  const isEdit = !!service;
  const [form, setForm] = useState({
    name: service?.name ?? "",
    category: service?.category ?? "",
    description: service?.description ?? "",
    price: service?.price ?? "",
    tags: service?.tags?.join(", ") ?? "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Введите название услуги");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        name: form.name.trim(),
        category: form.category.trim(),
        description: form.description.trim(),
        price: form.price.trim(),
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      });
      toast.success(isEdit ? "Услуга обновлена" : "Услуга добавлена");
      onClose();
    } catch {
      toast.error("Не удалось сохранить услугу");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-[15px] text-gray-900">{isEdit ? "Редактировать услугу" : "Добавить услугу"}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Название услуги*</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Напр.: Внедрение CRM, аудит SOC..."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">Категория</label>
              <input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="Консалтинг, интеграция..."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">Стоимость / диапазон</label>
              <input
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="от 100 000 ₽"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Описание</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Что входит в услугу, для кого она подходит..."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
            />
          </div>

          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Теги (через запятую)</label>
            <input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="консалтинг, внедрение"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <div className="p-3 rounded-xl bg-sky-50 text-[12px] text-sky-700 flex items-start gap-2">
            <Info size={13} className="mt-0.5 flex-shrink-0" />
            <span>Услуги отображаются в профиле стенда рядом с товарами. Лимит — 5 позиций.</span>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100">Отмена</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] disabled:opacity-60">
            {isEdit ? "Сохранить" : "Добавить"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ExhibitorServicesPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editService, setEditService] = useState<Service | undefined>(undefined);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  const eventId = user?.assignedEventId ?? "";
  const companyId = user?.assignedCompanyId ?? "";
  const maxServices = 5;

  const loadServices = useCallback(async () => {
    if (!eventId || !companyId) return;
    setLoading(true);
    try {
      const list = await catalogService.listServices(eventId, companyId, "admin");
      setServices(list);
    } catch {
      toast.error("Не удалось загрузить услуги");
    } finally {
      setLoading(false);
    }
  }, [eventId, companyId]);

  useEffect(() => {
    void loadServices();
  }, [loadServices]);

  const handleSave = async (data: {
    name: string;
    category: string;
    description: string;
    price: string;
    tags: string[];
  }) => {
    if (!eventId || !companyId) return;
    if (editService) {
      await catalogService.updateService(eventId, companyId, editService.id, data);
    } else {
      await catalogService.createService(eventId, companyId, data);
    }
    await loadServices();
  };

  const handleDelete = async (serviceId: string) => {
    if (!eventId || !companyId) return;
    try {
      await catalogService.deleteService(eventId, companyId, serviceId);
      toast.success("Услуга удалена");
      await loadServices();
    } catch {
      toast.error("Не удалось удалить услугу");
    }
  };

  const canAdd = services.length < maxServices;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-5">
      {showModal && (
        <ServiceModal
          service={editService}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}

      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/exhibitor")} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hidden sm:block">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-[19px] text-gray-900">Услуги на выставке</h1>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {services.length} из {maxServices} позиций
          </p>
        </div>
        {canAdd && (
          <button
            onClick={() => { setEditService(undefined); setShowModal(true); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px]"
          >
            <Plus size={14} /> Добавить
          </button>
        )}
      </div>

      <div className="p-3.5 rounded-xl bg-sky-50 border border-sky-100 text-[12px] text-sky-700">
        Услуги — консалтинг, внедрение, обслуживание. Посетители увидят их в каталоге стенда.
      </div>

      {loading ? (
        <div className="text-center py-12 text-[13px] text-gray-400">Загрузка…</div>
      ) : services.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 py-16 text-center">
          <Briefcase size={32} className="text-gray-200 mx-auto mb-3" />
          <p className="text-[14px] text-gray-500">Услуг пока нет</p>
          <button
            onClick={() => { setEditService(undefined); setShowModal(true); }}
            className="mt-4 px-4 py-2 rounded-xl bg-indigo-600 text-white text-[13px]"
          >
            + Добавить первую услугу
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <div key={service.id} className="bg-white rounded-2xl border border-gray-100 p-4 hover:border-sky-100 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                  <Briefcase size={20} className="text-sky-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[14px] text-gray-900">{service.name}</div>
                      <div className="text-[12px] text-sky-600">{service.category}</div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => { setEditService(service); setShowModal(true); }}
                        className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-500"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => void handleDelete(service.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="text-[12px] text-gray-500 mt-1 line-clamp-2">{service.description}</p>
                  {service.price && (
                    <div className="inline-flex items-center gap-1 mt-1.5 text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <DollarSign size={10} /> {service.price}
                    </div>
                  )}
                  {service.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {service.tags.map((tag) => (
                        <span key={tag} className="text-[10px] bg-gray-50 text-gray-500 px-2 py-0.5 rounded-md border border-gray-100">
                          <Tag size={9} className="inline mr-0.5" />{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
